import React from "react";
import { Link } from "react-router-dom";
import { waLink, LOGO_SRC } from "@/lib/brand";
import Mnemonic from "@/components/Mnemonic";
import SaveNumberLink from "@/components/SaveNumberLink";

const VALUES = [
  {
    key: "Rootedness",
    text: "Connected to the land and its traditions.",
  },
  {
    key: "Regeneration",
    text: "Committed to ecological and economic well-being.",
  },
  {
    key: "Storytelling",
    text: "Sharing the narratives of the place and its people.",
  },
  {
    key: "Simplicity",
    text: "Embracing natural, uncomplicated experiences.",
  },
  {
    key: "Community",
    text: "Fostering connection and shared journeys.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO — dark */}
      <section
        className="relative"
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24 md:pb-36 text-center">
          <Mnemonic size={54} className="mx-auto mb-8 reveal" />
          <div
            className="italic-display reveal mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "rgba(196,148,59,0.85)" }}
            data-testid="hero-bengali"
          >
            সুন্দরবন · পশ্চিমবঙ্গ
          </div>
          <div className="eyebrow reveal" style={{ color: "var(--sundari-gold)" }} data-testid="hero-eyebrow">
            AN AGRI-ECOLOGICAL RETREAT · SUNDARBANS · WEST BENGAL
          </div>
          <h1
            className="italic-display reveal-2 mt-8 sr-only"
            data-testid="hero-wordmark-text"
          >
            FarmStory
          </h1>
          <img
            src={LOGO_SRC}
            alt="FarmStory"
            data-testid="hero-wordmark"
            className="reveal-2 mt-6 mx-auto block"
            style={{
              width: "min(720px, 82vw)",
              height: "auto",
            }}
          />
          <div className="flex items-center justify-center mt-8 reveal-3">
            <span className="divider-gold" />
          </div>
          <p
            className="mt-8 reveal-3"
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "clamp(20px, 2.6vw, 28px)",
              color: "rgba(242,233,212,0.92)",
              letterSpacing: "0.01em",
            }}
            data-testid="hero-tagline"
          >
            Rooted in wonder
          </p>

          <div className="mt-14 flex flex-wrap gap-4 justify-center items-center reveal-4">
            <a
              href={waLink("Hello FarmStory, I'd like to plan a visit.")}
              target="_blank"
              rel="noreferrer"
              className="btn-pill btn-pill--solid"
              data-testid="hero-cta-whatsapp"
            >
              Message on WhatsApp
            </a>
            <Link to="/farm" className="btn-pill" data-testid="hero-cta-explore">
              Walk the Farm
            </Link>
          </div>
          <div className="mt-6 flex justify-center reveal-4">
            <SaveNumberLink tone="light" testId="hero-save-number" />
          </div>

          <div
            className="mt-24 max-w-[720px] mx-auto reveal-5"
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(16px, 1.6vw, 20px)",
              color: "rgba(242,233,212,0.72)",
              lineHeight: 1.55,
            }}
          >
            The gawa outside your cottage has been here longer than the cottage. The Muscovy ducks
            will tell you when morning has arrived. They are not wrong.
          </div>
        </div>
      </section>

      {/* OPENER — the verse */}
      <section
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
        className="hair-t-light"
        data-testid="hero-opener"
      >
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <div className="eyebrow" style={{ color: "var(--sundari-gold)", opacity: 0.7 }}>
            An Opening
          </div>
          <div className="mx-auto mt-6 divider-gold" />
          <p
            className="italic-display mt-10"
            style={{
              fontSize: "clamp(20px, 2.4vw, 30px)",
              lineHeight: 1.6,
              color: "rgba(242,233,212,0.92)",
            }}
          >
            Where rivers lose their names and become the sea,
            <br />
            The Sundarbans breathes in the hush of eternity.
            <br />
            Mangrove roots cradle both tide and time,
            <br />
            While tigers walk like whispered legends through the green sublime.
            <br />
            Here, every dawn is written by wind, water and prayer,
            <br />
            And every heart that enters leaves a part of itself there.
          </p>
        </div>
      </section>

      {/* PHILOSOPHY — two column */}
      <section
        className="hair-b"
        style={{ background: "var(--salt-white)", color: "var(--mangrove-night)" }}
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>The Place</div>
            <h2 className="italic-display mt-5" style={{ fontSize: "clamp(38px, 5vw, 60px)", lineHeight: 1.02 }}>
              Where rivers lose their names and become the sea.
            </h2>
            <hr className="divider-gold mt-8" />
          </div>
          <div className="md:col-span-7 space-y-6" style={{ fontSize: "16px", lineHeight: 1.75 }}>
            <p>
              FarmStory sits at the edge of the world&rsquo;s greatest mangrove delta. Brackish crab
              ponds, freshwater gawa, golpata palms, banana groves, tomato fields, and a mangrove
              nursery form the living landscape of each stay.
            </p>
            <p>
              Twin Suites sit at the front of the property. Jungle Villas rise on a raised deck
              across the gawa, held in a small canopy of golpata and other native trees of the
              delta. Both stand lightly on the earth.
            </p>

            <blockquote
              className="mt-10 md:mt-14 pl-6"
              style={{
                borderLeft: "1px solid var(--sundari-gold)",
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: "clamp(22px, 2.4vw, 30px)",
                lineHeight: 1.35,
              }}
              data-testid="home-pullquote"
            >
              &ldquo;The Sundarbans has always known how to tell a story. We simply learned to
              listen.&rdquo;
            </blockquote>
          </div>
        </div>
      </section>

      {/* FIVE VALUES — dark band */}
      <section
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
        data-testid="values-band"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="eyebrow mb-10" style={{ color: "var(--sundari-gold)" }}>Our Values</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
            {VALUES.map((v, i) => (
              <div key={v.key} className="hair-t-light pt-6" data-testid={`value-${v.key.toLowerCase()}`}>
                <div className="label-track" style={{ color: "var(--sundari-gold)" }}>0{i + 1}</div>
                <h4 className="italic-display mt-3" style={{ fontSize: "26px", lineHeight: 1.1 }}>
                  {v.key}
                </h4>
                <p className="mt-3" style={{ fontSize: "13.5px", opacity: 0.78, lineHeight: 1.65 }}>
                  {v.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
